import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { Loader } from 'component/common/Loader/Loader';
import { ActivityGqlOperation } from 'store/api/api.types';
import { withErrorBoundary } from 'component/common/Hoc/ErrorBoundary';
import { PageContentStatus } from 'core/type/PageContentStatus';
import { CmsProps } from 'core/type/BootstrapConfig.types';
import { closeAllOverlays } from 'store/slice/overlay';
import { StickyTopBar } from 'component/common/StickyTopBar/StickyTopBar';
import { ActivitiesFooter } from 'component/common/Footer/ActivitiesFooter';
import { DetailsPageProps, mapStateToProps } from 'component/page/detail/DetailsPage.selectors';
import { TabsPlaceHolder } from 'component/common/PlaceHolder/TabsPlaceHolder/TabsPlaceHolder';
import { trackPageVisitEvent } from 'core/messaging/analytics/analytics';
import { DetailsPageContextFields } from 'core/messaging/analytics/pages/pageEventsMapping';
import StandardErrorPage from 'component/page/error/ActivityStandardErrorPage';
import NoDetailsErrorPage from 'component/page/error/ActivityNoDetailsErrorPage';
import SearchBar from 'component/common/SearchBar/SearchBar';
import DeprecatedDetailPageContent from 'component/page/deprecatedDetail/DetailPageContent/DetailPageContent';
import DetailPageContent from 'component/page/detail/DetailPageContent/DetailPageContent';
import DetailPageTabsComponent from 'component/page/detail/DetailPageTabs/DetailPageTabs';
import TYPRedirectBanner from 'component/common/TYPRedirectBanner/TYPRedirectBanner';
import CartInterstitialContainer from 'component/page/detail/CartInterstitialSheet/CartInterstitialContainer';
import { resetReviewSortFilter } from 'component/page/detail/store/review';
import queryActivitiesGqlApi from 'store/api/activitiesGqlApi';
import { getTravelDateFromQueryParams, mapActivityTokenByRepresentativeInfo } from 'gql/service/graph/activityService';
import { OrchestrateTopActivitiesInSessionStorage } from 'core-framework/util/EmptyStateSuggestion';
import SeoLdJson from 'component/page/detail/SEOLdJson/SEOLdJson';
import Feature from 'component/common/Experiment/Feature';
import { FeatureName } from 'core/constant/FeatureName';
import { isOn } from 'core/window/bootstrapConfig';
import { FloatingPanelProvider } from 'component/page/detail/FloatingSidePanel/FloatingPanelContext';
import { useLocation } from 'react-router-dom';
import { getDateInYmdFormat } from 'core-framework/util/datetime/date';
import { addDays, startOfMonth } from 'date-fns';
import { setSelectedDateStateFromQueryParams } from 'component/page/detail/store/availabilityIndex';
import { DetailPageModularize } from 'component/page/detail/DetailPageModularize/DetailPageModularize';

const CALENDAR_RANGE_DAYS = 30;

const DetailPageContentWrapper: React.FC<{ contentStatus: PageContentStatus; isDisableActivitiesForCnOrigin: boolean } & CmsProps> = React.memo((props) => {
    const isRedirectGenericErrorComponent = isOn(FeatureName.RedirectGenericErrorComponent);
    if (props.contentStatus === PageContentStatus.Loading) {
        return <Loader element="detail-page" />;
    }
    if (props.contentStatus === PageContentStatus.StandardError || props.isDisableActivitiesForCnOrigin) {
        return <StandardErrorPage page="details" contentStatus={props.contentStatus} isTargetToSearchPage={true} />;
    }

    if (props.contentStatus === PageContentStatus.SupplyError) {
        return isRedirectGenericErrorComponent ? (
            <StandardErrorPage page="details" contentStatus={props.contentStatus} isTargetToSearchPage={true} />
        ) : (
            <NoDetailsErrorPage error={props.cms[126985]} buttonText={props.cms[130767]} contentStatus={props.contentStatus} />
        );
    }

    return (
        <>
            <Feature.Off name={FeatureName.DetailPageRevamp}>
                <DeprecatedDetailPageContent />
            </Feature.Off>
            <Feature.On name={FeatureName.DetailPageRevamp}>
                <FloatingPanelProvider>
                    <DetailPageContent />
                </FloatingPanelProvider>
            </Feature.On>
        </>
    );
});
DetailPageContentWrapper.displayName = 'DetailPageContentWrapper';

const DetailsPageComponent: React.FC<DetailsPageProps> = (props) => {
    const dispatch = useDispatch();
    const { contentStatus, config, isLoading, activityId, loyaltySearchType, isPrerender, isSearchBoxFeatureGroup, isContinueYourBookingFeature, isDisableActivitiesForCnOrigin } =
        props;
    const currencyCode = config.currency.code;
    const locationSearch = useLocation().search;
    const travelDateFromQueryParams = getTravelDateFromQueryParams(locationSearch);
    const firstDateOfMonth = travelDateFromQueryParams && startOfMonth(travelDateFromQueryParams);
    const endDateOfMonth = firstDateOfMonth && addDays(firstDateOfMonth, CALENDAR_RANGE_DAYS);
    const startDate = firstDateOfMonth && getDateInYmdFormat(firstDateOfMonth);
    const endDate = endDateOfMonth && getDateInYmdFormat(endDateOfMonth);

    React.useEffect(() => {
        if (isSearchBoxFeatureGroup) {
            OrchestrateTopActivitiesInSessionStorage(config);
        }
    }, [config, isSearchBoxFeatureGroup]);

    React.useEffect(() => {
        dispatch(closeAllOverlays());
        dispatch(resetReviewSortFilter());
        if (activityId <= 0) {
            return;
        }

        if (travelDateFromQueryParams && isContinueYourBookingFeature) {
            dispatch(setSelectedDateStateFromQueryParams({ locationSearch }));
        }

        queryActivitiesGqlApi({
            args: { props: { activityId, loyaltySearchType } },
            operation: ActivityGqlOperation.details,
            onSuccess: (operation, args, detailsResponse) => {
                // Need to use result from Axios because onSuccess is triggered before details store is updated
                const activityToken = mapActivityTokenByRepresentativeInfo(detailsResponse?.result?.activity?.activityRepresentativeInfo);
                if (!isPrerender && activityToken) {
                    if (isContinueYourBookingFeature) {
                        queryActivitiesGqlApi({ args: { props: { activityToken, startDate, endDate } }, operation: ActivityGqlOperation.calendar });
                    } else {
                        queryActivitiesGqlApi({ args: { props: { activityToken } }, operation: ActivityGqlOperation.calendar });
                    }
                }
            },
        });
        queryActivitiesGqlApi({ args: { props: { activityId } }, operation: ActivityGqlOperation.review });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currencyCode]);

    React.useEffect(() => {
        if (contentStatus === PageContentStatus.Success) {
            trackPageVisitEvent<DetailsPageContextFields>({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentStatus]);

    return (
        <>
            <SeoLdJson />
            <TYPRedirectBanner />
            <StickyTopBar
                elementName="activities-header-search-bar"
                data-testid="activities-header-search-bar"
                sticky={false}
                contentAreaProps={{ px: '8', py: '8' }}
                noShadow={true}
            >
                <SearchBar />
            </StickyTopBar>
            <Feature.Off name={FeatureName.DetailPageForSimCard}>
                <Feature.Off name={FeatureName.DetailPageRevamp}>
                    <StickyTopBar data-testid="activities-nav-tabs-container" contentAreaProps={{ px: '8', py: '0' }} zIndex={3}>
                        {isLoading ? <TabsPlaceHolder element="detail-tab" item={5} /> : <DetailPageTabsComponent />}
                    </StickyTopBar>
                </Feature.Off>
                <DetailPageContentWrapper contentStatus={contentStatus} cms={config.cms} isDisableActivitiesForCnOrigin={isDisableActivitiesForCnOrigin} />
            </Feature.Off>
            <Feature.On name={FeatureName.DetailPageForSimCard}>
                <DetailPageModularize isDisableActivitiesForCnOrigin={isDisableActivitiesForCnOrigin} />
            </Feature.On>
            <CartInterstitialContainer />
            <ActivitiesFooter />
        </>
    );
};
DetailsPageComponent.displayName = 'DetailsPageComponent';

const DetailsPage = withErrorBoundary(DetailsPageComponent);
DetailsPage.displayName = 'DetailsPage';

export default connect(mapStateToProps)(DetailsPage);
